import { useModel as _useModel, mergeModels as _mergeModels, defineComponent as _defineComponent } from 'vue'
import { toDisplayString as _toDisplayString, createTextVNode as _createTextVNode, unref as _unref, withCtx as _withCtx, createVNode as _createVNode, openBlock as _openBlock, createElementBlock as _createElementBlock, createCommentVNode as _createCommentVNode, createBlock as _createBlock, createElementVNode as _createElementVNode, withModifiers as _withModifiers } from "vue"

const _hoisted_1 = { class: "flex flex-col gap-y-4 pt-2" }
const _hoisted_2 = {
  key: 0,
  class: "p-8 flex justify-center items-center"
}

import { computed, nextTick, ref, watch } from 'vue';
import { VisuallyHidden } from 'radix-vue';
import { ColumnDef, SortingState } from '@tanstack/vue-table';
import { parseISO } from 'date-fns';
import { Trash2 } from 'lucide-vue-next';
import { cloneDeep } from 'lodash-es';
import CustomButton from '@/components/ui/CustomButton.vue';
import { Dialog, DialogHeader, DialogTitle, DialogDescription, DialogFooter, DialogClose } from '@/components/ui/dialog';
import { i18n } from '@/plugins/internationalization/i18n';
import { useStudyPeriodSetups } from '@/plugins/store/actions/queries/study-periods';
import { StudyPeriodCategory, StudyPeriodSetup } from '@/types';
import { DataTable } from '@/components/ui/data-table';
import SearchInput from '@/components/ui/input/SearchInput.vue';
import { Card } from '@/components/ui/card';
import { Checkbox } from '@/components/ui/checkbox';
import { Button } from '@/components/ui/button';
import Spinner from '@/components/ui/Spinner.vue';
import DeleteSavedAnalysisDialog from './DeleteSavedAnalysisDialog.vue';

interface Props {
    categories: StudyPeriodCategory[];
}

interface RowItem extends StudyPeriodSetup {
    _displayCategories: string;
    _displayTeams: string;
    _displayFavoritTeam: string;
    _displayGrowthReference: string;
}


export default /*@__PURE__*/_defineComponent({
  __name: 'SavedAnalysesDialog',
  props: /*@__PURE__*/_mergeModels({
    categories: {}
  }, {
    "open": { type: Boolean, ...{ required: true } },
    "openModifiers": {},
  }),
  emits: /*@__PURE__*/_mergeModels(["generate"], ["update:open"]),
  setup(__props: any, { emit: __emit }) {

const props = __props;
const emit = __emit;

const isOpen = _useModel<boolean>(__props, 'open');

const { data: studyPeriodSetups, isLoading } = useStudyPeriodSetups();

const page = ref(1);
const pageSize = ref(9999);
const pageSizeOptions = ref([10, 25, 50, 100]);

const columns = computed<ColumnDef<RowItem>[]>(() => [
    {
        header: '',
        accessorKey: '_selection',
        meta: {
            cellClass: 'w-10',
        },
    },
    {
        header: i18n.global.t('views.monitoring.teamComparison.dialogs.savedAnalyses.table.headers.name'),
        accessorKey: 'name',
        enableSorting: true,
        enableGlobalFilter: true,
    },
    {
        header: i18n.global.t('views.monitoring.teamComparison.dialogs.savedAnalyses.table.headers.categories'),
        accessorKey: '_displayCategories',
        enableSorting: true,
        enableGlobalFilter: true,
    },
    {
        header: i18n.global.t('views.monitoring.teamComparison.dialogs.savedAnalyses.table.headers.teams'),
        accessorKey: '_displayTeams',
        enableSorting: true,
        enableGlobalFilter: true,
    },
    {
        header: i18n.global.t('views.monitoring.teamComparison.dialogs.savedAnalyses.table.headers.favoriteTeam'),
        accessorKey: '_displayFavoritTeam',
        enableSorting: true,
        enableGlobalFilter: true,
    },
    {
        header: i18n.global.t('views.monitoring.teamComparison.dialogs.savedAnalyses.table.headers.growthReference'),
        accessorKey: '_displayGrowthReference',
        enableSorting: true,
        enableGlobalFilter: true,
    },
    {
        header: i18n.global.t('views.monitoring.teamComparison.dialogs.savedAnalyses.table.headers.animalGroup'),
        accessorKey: '_displayAnimalGroup',
        enableSorting: true,
    },
    {
        header: i18n.global.t('views.monitoring.teamComparison.dialogs.savedAnalyses.table.headers.creationDate'),
        accessorKey: 'created_at_utc',
        enableSorting: true,
    },
    {
        header: '',
        accessorKey: '_actions',
        meta: {
            cellClass: 'w-10',
        },
    },
]);

function getDisplayAnimalGroup(setup: StudyPeriodSetup) {
    switch (setup.animal_group_id) {
        case 1:
            return i18n.global.t('views.monitoring.teamComparison.fields.animalGroup.options.weaners');
        case 2:
            return i18n.global.t('views.monitoring.teamComparison.fields.animalGroup.options.slaughterPigs');
        case 3:
            return i18n.global.t('views.monitoring.teamComparison.fields.animalGroup.options.frats');
        default:
            return '-';
    }
}

const items = computed(() =>
    studyPeriodSetups.value?.map((setup) => ({
        ...setup,
        _displayCategories: props.categories
            .filter((category) => category.options.some((option) => setup.categories.includes(option.id)))
            .flatMap((category) => category.options.filter((option) => setup.categories.includes(option.id)).map((option) => option.name))
            .join(', '),
        _displayTeams: setup.study_periods.map((period) => period.name).join(', '),
        _displayFavoritTeam: setup.favorites.map((period) => period.name).join(', ') || '-',
        _displayGrowthReference: setup.growth_reference?.name || '-',
        _displayAnimalGroup: getDisplayAnimalGroup(setup),
    }))
);

const filter = ref('');
const sorting = ref<SortingState>([
    {
        id: 'created_at_utc',
        desc: true,
    },
]);

const selectedSetup = ref<number | null>(null);

watch(
    studyPeriodSetups,
    async (newVal) => {
        if (newVal) {
            if (selectedSetup.value && newVal.some((setup) => setup.id === selectedSetup.value) && isOpen.value) {
                return;
            }

            const sortedSetups = cloneDeep(newVal).sort((a, b) => new Date(b.created_at_utc).getTime() - new Date(a.created_at_utc).getTime());

            selectedSetup.value = sortedSetups[0]?.id || null;
        }
    },
    { immediate: true }
);

function handleSelectionChange(id: number) {
    selectedSetup.value = id;
}

const studyPeriodSetupToDelete = ref<StudyPeriodSetup | null>(null);
const isDeleteDialogOpen = ref(false);

async function handleDelete(studyPeriodSetup: StudyPeriodSetup) {
    studyPeriodSetupToDelete.value = studyPeriodSetup;

    await nextTick();

    isDeleteDialogOpen.value = true;
}

function handleGenerate() {
    const selectedSetupToGenerate = items.value?.find((item) => item.id === selectedSetup.value);

    if (selectedSetupToGenerate) {
        emit('generate', selectedSetupToGenerate);
    }

    isOpen.value = false;
}

return (_ctx: any,_cache: any) => {
  return (_openBlock(), _createBlock(_unref(Dialog), {
    modelValue: isOpen.value,
    "onUpdate:modelValue": _cache[4] || (_cache[4] = ($event: any) => ((isOpen).value = $event)),
    class: "sm:max-w-screen-xl",
    onOpenAutoFocus: _cache[5] || (_cache[5] = _withModifiers(() => {}, ["prevent"]))
  }, {
    default: _withCtx(() => [
      _createVNode(_unref(DialogHeader), null, {
        default: _withCtx(() => [
          _createVNode(_unref(DialogTitle), null, {
            default: _withCtx(() => [
              _createTextVNode(_toDisplayString(_ctx.$t('views.monitoring.teamComparison.dialogs.savedAnalyses.title')), 1)
            ]),
            _: 1
          }),
          _createVNode(_unref(VisuallyHidden), null, {
            default: _withCtx(() => [
              _createVNode(_unref(DialogDescription), null, {
                default: _withCtx(() => [
                  _createTextVNode(_toDisplayString(_ctx.$t('views.monitoring.teamComparison.dialogs.savedAnalyses.description')), 1)
                ]),
                _: 1
              })
            ]),
            _: 1
          })
        ]),
        _: 1
      }),
      _createElementVNode("div", _hoisted_1, [
        _createVNode(SearchInput, {
          modelValue: filter.value,
          "onUpdate:modelValue": _cache[0] || (_cache[0] = ($event: any) => ((filter).value = $event)),
          placeholder: _ctx.$t('views.monitoring.teamComparison.dialogs.savedAnalyses.fields.search.placeholder'),
          class: "w-auto min-w-64 max-w-full"
        }, null, 8, ["modelValue", "placeholder"]),
        (_unref(isLoading))
          ? (_openBlock(), _createElementBlock("div", _hoisted_2, [
              _createVNode(Spinner, { class: "h-10 w-10" })
            ]))
          : (_openBlock(), _createBlock(_unref(Card), {
              key: 1,
              class: "saved-analyses-table overflow-hidden"
            }, {
              default: _withCtx(() => [
                _createVNode(_unref(DataTable), {
                  sorting: sorting.value,
                  "onUpdate:sorting": _cache[1] || (_cache[1] = ($event: any) => ((sorting).value = $event)),
                  filter: filter.value,
                  "onUpdate:filter": _cache[2] || (_cache[2] = ($event: any) => ((filter).value = $event)),
                  data: items.value as RowItem[],
                  columns: columns.value,
                  page: page.value,
                  "page-size": pageSize.value,
                  "page-size-options": pageSizeOptions.value,
                  "enable-pagination": false
                }, {
                  "item[_selection]": _withCtx(({ item }) => [
                    _createVNode(_unref(Checkbox), {
                      checked: selectedSetup.value === item.id,
                      variant: "circle",
                      class: "my-1",
                      "onUpdate:checked": ($event: any) => (handleSelectionChange(item.id))
                    }, null, 8, ["checked", "onUpdate:checked"])
                  ]),
                  "item[created_at_utc]": _withCtx(({ item }) => [
                    _createTextVNode(_toDisplayString(_ctx.$d(_unref(parseISO)(item.created_at_utc), 'short')), 1)
                  ]),
                  "item[_actions]": _withCtx(({ item }) => [
                    _createVNode(_unref(Button), {
                      variant: "ghost",
                      size: "icon",
                      class: "text-red-500 hover:text-red-600 hover:bg-red-100 -mr-1",
                      onClick: ($event: any) => (handleDelete(item))
                    }, {
                      default: _withCtx(() => [
                        _createVNode(_unref(Trash2), { class: "w-4 h-4" })
                      ]),
                      _: 2
                    }, 1032, ["onClick"])
                  ]),
                  _: 1
                }, 8, ["sorting", "filter", "data", "columns", "page", "page-size", "page-size-options"])
              ]),
              _: 1
            }))
      ]),
      (studyPeriodSetupToDelete.value)
        ? (_openBlock(), _createBlock(DeleteSavedAnalysisDialog, {
            key: 0,
            open: isDeleteDialogOpen.value,
            "onUpdate:open": _cache[3] || (_cache[3] = ($event: any) => ((isDeleteDialogOpen).value = $event)),
            "study-period-setup": studyPeriodSetupToDelete.value
          }, null, 8, ["open", "study-period-setup"]))
        : _createCommentVNode("", true),
      _createVNode(_unref(DialogFooter), { class: "flex sm:justify-center pt-2" }, {
        default: _withCtx(() => [
          _createVNode(_unref(DialogClose), { "as-child": "" }, {
            default: _withCtx(() => [
              _createVNode(CustomButton, {
                "color-preset": "white",
                class: "w-auto min-w-44"
              }, {
                default: _withCtx(() => [
                  _createTextVNode(_toDisplayString(_ctx.$t('views.monitoring.teamComparison.dialogs.savedAnalyses.buttons.cancel')), 1)
                ]),
                _: 1
              })
            ]),
            _: 1
          }),
          _createVNode(CustomButton, {
            class: "w-auto min-w-44",
            disabled: selectedSetup.value === null,
            onClick: handleGenerate
          }, {
            default: _withCtx(() => [
              _createTextVNode(_toDisplayString(_ctx.$t('views.monitoring.teamComparison.dialogs.savedAnalyses.buttons.generate')), 1)
            ]),
            _: 1
          }, 8, ["disabled"])
        ]),
        _: 1
      })
    ]),
    _: 1
  }, 8, ["modelValue"]))
}
}

})